import { supabase } from '../lib/supabase';

const isSchemaError = (error) => {
  if (!error) return false;

  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }

  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }

  return false;
};

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

export const workOrderService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('work_orders')?.select(`
          *,
          vehicle:vehicles(*),
          client:clients(*),
          created_by_user:user_profiles!work_orders_created_by_fkey(id, full_name, email),
          assigned_to_user:user_profiles!work_orders_assigned_to_fkey(id, full_name, email)
        `)?.order('fecha_ingreso', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching work orders:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch work orders:', error);
      throw error;
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('work_orders')?.select(`
          *,
          vehicle:vehicles(*),
          client:clients(*),
          created_by_user:user_profiles!work_orders_created_by_fkey(id, full_name, email),
          assigned_to_user:user_profiles!work_orders_assigned_to_fkey(id, full_name, email)
        `)?.eq('id', id)?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error fetching work order:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to fetch work order:', error);
      throw error;
    }
  },

  async getByFolio(folio) {
    try {
      const { data, error } = await supabase?.from('work_orders')?.select(`
          *,
          vehicle:vehicles(*),
          client:clients(*),
          created_by_user:user_profiles!work_orders_created_by_fkey(id, full_name, email),
          assigned_to_user:user_profiles!work_orders_assigned_to_fkey(id, full_name, email)
        `)?.eq('folio_ot', folio)?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error fetching work order by folio:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to fetch work order by folio:', error);
      throw error;
    }
  },

  async create(workOrderData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Usuario no autenticado');

      const snakeData = toSnakeCase({
        ...workOrderData,
        createdBy: user?.id
      });

      const { data, error } = await supabase?.from('work_orders')?.insert(snakeData)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }
      try {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', 'orden_creada', {
      event_category: 'work_orders',
      order_id: data?.id,
      value: Number(snakeData?.total || 0),
      currency: 'MXN',
    });
  }
} catch (e) {
  // no rompemos la app por analytics
  console.log('GA event error:', e);
}
      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to create work order:', error);
      throw error;
    }
  },

  async update(id, workOrderData) {
    try {
      const snakeData = toSnakeCase(workOrderData);

      const { data, error } = await supabase?.from('work_orders')?.update(snakeData)?.eq('id', id)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to update work order:', error);
      throw error;
    }
  },

  async updateStatus(id, newStatus) {
    try {
      const { data, error } = await supabase?.from('work_orders')?.update({ estado_ot: newStatus })?.eq('id', id)?.select(`
          *,
          vehicle:vehicles(*),
          client:clients(*),
          assigned_to_user:user_profiles!work_orders_assigned_to_fkey(id, full_name, email)
        `)?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to update work order status:', error);
      throw error;
    }
  },

  async createTimelineEvent(eventData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      const snakeData = toSnakeCase({
        workOrderId: eventData?.workOrderId,
        stage: eventData?.stage,
        status: eventData?.status,
        findings: eventData?.findings || null,
        responsiblePerson: user?.id || null,
        timestamp: new Date()?.toISOString()
      });

      const { data, error } = await supabase?.from('timeline_events')?.insert(snakeData)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to create timeline event:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('work_orders')?.delete()?.eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return true;
    } catch (error) {
      console.error('Failed to delete work order:', error);
      throw error;
    }
  },

  async getByStatus(status) {
    try {
      const { data, error } = await supabase?.from('work_orders')?.select(`
          *,
          vehicle:vehicles(*),
          client:clients(*)
        `)?.eq('estado_ot', status)?.order('fecha_ingreso', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching work orders by status:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch work orders by status:', error);
      throw error;
    }
  }
};